import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EyeOffIcon, LockIcon, ShieldCheckIcon } from 'lucide-react';
import { AdminEmptyState, AdminMetricCard, AdminPageTitle, AdminTabs, ConfirmationModal, Panel, PermissionGate, StatusBadge, UserCell } from '../../components/admin/AdminPrimitives';
import { hasCapability, integrationStatusLabels, prototypeAdminRole, userConnections, userHydrationOps } from '../../data/adminOperations';
import { formatLiters } from '../../utils/hydration';
import { AuditEntry } from '../../components/admin/AdminPrimitives';
import { Button } from '../../components/ui/Button';
import { adminUsers, auditLog, adminReports, moderationQueue, recordAudit, roleLabels } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
const tabs = [{
  id: 'account',
  label: 'Conta'
}, {
  id: 'activity',
  label: 'Atividade'
}, {
  id: 'content',
  label: 'Conteúdo público'
}, {
  id: 'moderation',
  label: 'Moderação'
}, {
  id: 'reports',
  label: 'Denúncias'
}, {
  id: 'gamification',
  label: 'Gamificação'
}, {
  id: 'roles',
  label: 'Papéis'
}, {
  id: 'security',
  label: 'Segurança'
}];
export function AdminUserDetail() {
  const {
    userId
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = useState('account');
  const [confirm, setConfirm] = useState(false);
  const [healthUnlocked, setHealthUnlocked] = useState(false);
  const user = adminUsers.find(item => item.id === userId) ?? adminUsers[0];
  const content = [...moderationQueue.post.filter(item => item.authorId === user.id), ...moderationQueue.pulse.filter(item => item.authorId === user.id)];
  const reports = adminReports.filter(report => report.authorId === user.id);
  const audit = auditLog.filter(entry => entry.entity === user.id);
  const hydration = userHydrationOps(Math.max(0, adminUsers.indexOf(user)));
  const connections = userConnections(user.id);
  return <div className="space-y-4" data-id="element-2240" data-mp="16npoe15zslpz">
      <AdminPageTitle title={user.name} subtitle={`${user.username} · cadastro em ${user.registered} · última atividade ${user.lastActive}`} action={<>
            <Button variant="outline" onClick={() => navigate('/admin/usuarios')} data-id="element-2242" data-mp="1flrngb6614am">
              Voltar à lista
            </Button>
            <Button variant="primary" onClick={() => setConfirm(true)} data-id="element-2243" data-mp="1plif853cd570">
              Suspender conta
            </Button>
          </>} data-id="element-2241" data-mp="16ohbsopfrd56" />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-2244" data-mp="1uisfkfs9r19m">
        <AdminMetricCard label="Status" value={user.status === 'suspended' ? 'Suspenso' : user.status === 'inactive' ? 'Inativo' : 'Ativo'} data-id="element-2245" data-mp="1zajyc456o2p7" />
        <AdminMetricCard label="Sequência atual" value={user.streak > 0 ? `${user.streak} ${user.streak === 1 ? 'dia' : 'dias'}` : '—'} accent={user.streak > 14} data-id="element-2246" data-mp="23bvn67g8gjwc" />
        <AdminMetricCard label="Denúncias recebidas" value={String(user.reports)} data-id="element-2247" data-mp="2da1revpii24d" />
        <AdminMetricCard label="Ações de moderação" value={String(user.moderationActions)} data-id="element-2248" data-mp="2nfb2bssl8kah" />
      </div>

      <AdminTabs tabs={tabs} active={tab} onChange={setTab} data-id="element-2249" data-mp="2if5eknpfvhxo" />

      {tab === 'account' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2250" data-mp="2dg2cnc4r9rtx">
          <Panel title="Identificação" data-id="element-2251" data-mp="2dif2scko38i2">
            <UserCell user={user} secondary={user.email} data-id="element-2252" data-mp="29fs720kvl3fl" />
            <dl className="mt-3 space-y-2" data-id="element-2253" data-mp="29hd0fmvgegbv">
              {[['Usuário', user.username], ['E-mail', user.email], ['Papel', user.role === 'admin' ? 'Admin' : 'Usuário'], ['Cadastro', user.registered], ['Última atividade', user.lastActive]].map(([label, value]) => <div key={label} className="flex items-baseline justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2254" data-mp="2sd355hurtqtg">
                  <dt className="text-[12px] text-muted" data-id="element-2255" data-mp="3k48z1gjwesaa">{label}</dt>
                  <dd className="text-[12.5px] font-medium text-deep" data-id="element-2256" data-mp="34mhok2wi8jxt">{value}</dd>
                </div>)}
            </dl>
          </Panel>

          <Panel title="Hidratação" hint="Dado operacional. Nenhum peso ou métrica corporal é exibido." data-id="element-2257" data-mp="39k11mzhguqo6">
            <PermissionGate allowed={hasCapability(prototypeAdminRole, 'hydration.viewOps')} area="dados operacionais de hidratação" data-id="element-2258" data-mp="2vofdsvmyycxc">
              <dl className="space-y-2" data-id="element-2259" data-mp="3ne23i23pstsr">
                {[['Peso corporal disponível', hydration.weightAvailable ? 'Sim' : 'Não informado'], ['Meta de hidratação atual', hydration.targetMl ? formatLiters(hydration.targetMl, 'pt-BR') : '— (sem peso, sem meta)'], ['Água registrada hoje', formatLiters(hydration.loggedTodayMl, 'pt-BR')], ['Método de conclusão', hydration.completion === 'automatic' ? 'Automático (meta atingida)' : hydration.completion === 'manual' ? 'Confirmação manual' : 'Não concluído']].map(([label, value]) => <div key={label} className="flex items-baseline justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2260" data-mp="24euj3zggzyzu">
                    <dt className="text-[12px] text-muted" data-id="element-2261" data-mp="1bqf3pbtffenz">{label}</dt>
                    <dd className="bee-num text-[12.5px] font-medium text-deep" data-id="element-2262" data-mp="1bs9majh86uow">{value}</dd>
                  </div>)}
              </dl>
            </PermissionGate>
          </Panel>

          <Panel title="Serviços conectados" hint="Nenhuma integração está ativa na plataforma." data-id="element-2263" data-mp="1lqfqx8uf2exg">
            <PermissionGate allowed={hasCapability(prototypeAdminRole, 'integrations.view')} area="status de integrações" data-id="element-2264" data-mp="324pe3zmx9xmp">
              {connections.length === 0 ? <AdminEmptyState title="Nenhum serviço conectado" body="Provedor, status da conexão e última sincronização aparecem aqui quando houver integrações ativas." data-id="element-2265" data-mp="3492lr3q6j0yl" /> : <ul className="space-y-2" data-id="element-2266" data-mp="3j2xnlprx3q0a">
                  {connections.map(connection => <li key={connection.provider} className="flex items-center justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2267" data-mp="2lh9o2w060rkj">
                      <span className="text-[12.5px] font-medium text-deep" data-id="element-2268" data-mp="2vd39z5zrxxez">{connection.provider}</span>
                      <span className="bee-num text-[11.5px] text-muted" data-id="element-2269" data-mp="306fnajz06hsf">{connection.lastSync ?? '—'}</span>
                      <StatusBadge status={connection.status} label={tl(integrationStatusLabels[connection.status])} data-id="element-2270" data-mp="22nml2ibd4yni" />
                    </li>)}
                </ul>}
            </PermissionGate>
          </Panel>

          <Panel title="Dados sensíveis de saúde" hint="Biomarcadores, testosterona e composição corporal." data-id="element-2271" data-mp="ft0wbqlj1voc">
            {!hasCapability(prototypeAdminRole, 'health.viewSensitive') ? <PermissionGate allowed={false} area="dados sensíveis de saúde" data-id="element-2272" data-mp="awaht7pdf851">
                {null}
              </PermissionGate> : healthUnlocked ? <div data-id="element-2273" data-mp="13bjgy42o388">
                <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-deep" data-id="element-2274" data-mp="120dqsr2gleh">
                  <ShieldCheckIcon size={15} className="mt-[1px] shrink-0 text-gold" aria-hidden="true" data-id="element-2275" data-mp="3t85oj01dbmg4" />
                  Acesso concedido apenas para atendimento de suporte. Este evento foi
                  registrado na auditoria com o seu nome e horário.
                </p>
                <ul className="mt-3 space-y-2" data-id="element-2276" data-mp="3eby541xsap1w">
                  {[['Registros de Daily Check (90d)', '74 dias'], ['Conclusões em 100 Bee', '41 dias'], ['Métricas corporais publicadas', 'Nenhuma']].map(([label, value]) => <li key={label} className="flex items-baseline justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2277" data-mp="x0qu8zoi3nh9">
                      <span className="text-[12px] text-muted" data-id="element-2278" data-mp="n9cm63ihhfci">{label}</span>
                      <span className="bee-num text-[12.5px] font-medium text-deep" data-id="element-2279" data-mp="s3ql2fwcu4lx">{value}</span>
                    </li>)}
                </ul>
              </div> : <div className="flex flex-col items-start gap-3" data-id="element-2280" data-mp="jadiem8hnomn">
                <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-muted" data-id="element-2281" data-mp="4c334nh2m3vs">
                  <EyeOffIcon size={15} className="mt-[1px] shrink-0" aria-hidden="true" data-id="element-2282" data-mp="9c7ho7yf4ojd" />
                  Informações de saúde ficam ocultas por padrão. A liberação exige
                  justificativa de suporte, gera registro na auditoria e não expõe
                  composição corporal.
                </p>
                <Button variant="outline" onClick={() => setHealthUnlocked(true)} data-id="element-2283" data-mp="3vuhkhpcxzclb">
                  <LockIcon size={15} data-id="element-2284" data-mp="3qttvtac6r7u9" />
                  Solicitar acesso auditado
                </Button>
              </div>}
          </Panel>
        </div>}

      {tab === 'activity' && <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-2285" data-mp="4ny9oa73vmn0">
          <AdminMetricCard label="Sessões (30d)" value="42" delta={3.1} data-id="element-2286" data-mp="iejwg9zokgq6" />
          <AdminMetricCard label="Daily Checks (30d)" value="26" delta={-4.2} data-id="element-2287" data-mp="ignzyqto3dpx" />
          <AdminMetricCard label="Posts (30d)" value="7" data-id="element-2288" data-mp="dg0babswv8yv" />
          <AdminMetricCard label="Pulses (30d)" value="31" delta={8.4} data-id="element-2289" data-mp="sc7tj6kre0bi" />
        </div>}

      {tab === 'content' && <Panel title="Conteúdo público" hint="Apenas o que já está visível na comunidade." data-id="element-2290" data-mp="nepldiekfp4k">
          {content.length === 0 ? <AdminEmptyState title="Sem conteúdo público" body="Este usuário não tem posts ou Pulses publicados no momento." data-id="element-2291" data-mp="1doyja3bsdre9" /> : <ul className="space-y-2" data-id="element-2292" data-mp="13xlph66fgr5l">
              {content.map(item => <li key={item.id} className="flex items-start justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2293" data-mp="zwiasn04915y">
                  <span className="min-w-0" data-id="element-2294" data-mp="1rnpvtlb0350a">
                    <span className="block text-[12.5px] text-deep" data-id="element-2295" data-mp="1rktvjqbok48o">{item.excerpt}</span>
                    <span className="bee-num block text-[11px] text-muted" data-id="element-2296" data-mp="1nn5xieeoxvck">
                      {item.kind === 'post' ? 'Post' : 'Pulse'} · {item.published}
                    </span>
                  </span>
                  <StatusBadge status={item.status} label={item.status} data-id="element-2297" data-mp="c3jootnh5qo6" />
                </li>)}
            </ul>}
        </Panel>}

      {tab === 'moderation' && <Panel title="Histórico de moderação" data-id="element-2298" data-mp="2is28a9cf87t4">
          {audit.length === 0 ? <AdminEmptyState title="Sem ações registradas" body="Nenhuma ação de moderação foi aplicada a esta conta." data-id="element-2299" data-mp="2nths5y2v489x" /> : <ul data-id="element-2300" data-mp="2wmczqyfsyg2v">
              {audit.map(entry => <AuditEntry key={entry.id} admin={entry.admin} role={tl(roleLabels[entry.role])} action={tl(entry.action)} entity={entry.entity} timestamp={entry.timestamp} context={tl(entry.context)} before={entry.before} after={entry.after} sensitive={entry.severity === 'sensitive'} data-id="element-2301" data-mp="31kwmv264txwt" />)}
            </ul>}
        </Panel>}

      {tab === 'reports' && <Panel title="Denúncias sobre este usuário" data-id="element-2302" data-mp="1l87snwkalq3b">
          {reports.length === 0 ? <AdminEmptyState title="Sem denúncias" body="Nenhuma denúncia registrada." data-id="element-2303" data-mp="2ojemwzcqgyv9" /> : <ul className="space-y-2" data-id="element-2304" data-mp="2jkk3bv6xz6a3">
              {reports.map(report => <li key={report.id} data-id="element-2305" data-mp="2xjbfvd65ojw4">
                  <button type="button" onClick={() => navigate(`/admin/denuncias/${report.id}`)} className="flex w-full items-start justify-between gap-3 border-b border-sand-line pb-2 text-left last:border-b-0" data-id="element-2306" data-mp="2tgn2qtaesqtc">
                    <span className="min-w-0" data-id="element-2307" data-mp="3h5pw2yeeo83o">
                      <span className="bee-num block text-[11px] font-bold text-gold-deep" data-id="element-2308" data-mp="3h4oba27l878r">
                        {report.id}
                      </span>
                      <span className="block text-[12.5px] text-deep" data-id="element-2309" data-mp="3q6e8xps2c2ez">
                        {tl(report.reason)}
                      </span>
                    </span>
                    <StatusBadge status={report.status} label={report.status} data-id="element-2310" data-mp="3l5hysuh4bk14" />
                  </button>
                </li>)}
            </ul>}
        </Panel>}

      {tab === 'gamification' && <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-2311" data-mp="3vsy9m5v6spit">
          <AdminMetricCard label="Bee hoje" value="75" hint="Base de 100 Bee" data-id="element-2312" data-mp="3vur7avntnzeo" />
          <AdminMetricCard label="Nível" value="Guerreiro" data-id="element-2313" data-mp="3nayl56raktu" />
          <AdminMetricCard label="Medalhas" value="4 de 5" accent data-id="element-2314" data-mp="3m2d4dzl0cmqx" />
          <AdminMetricCard label="Melhor sequência" value="27 dias" data-id="element-2315" data-mp="3m120lzrkbezt" />
        </div>}

      {tab === 'roles' && <Panel title="Papéis atribuídos" hint="Alterações de papel exigem confirmação e ficam auditadas." data-id="element-2316" data-mp="3c4yxorovzcbt">
          <div className="flex items-center justify-between gap-3" data-id="element-2317" data-mp="3h38x5ehn8z38">
            <span className="text-[13px] font-medium text-deep" data-id="element-2318" data-mp="3h1fm7mzzzspf">
              {user.role === 'admin' ? 'Admin' : 'Usuário do aplicativo'}
            </span>
            <Button variant="outline" onClick={() => navigate('/admin/papeis')} data-id="element-2319" data-mp="381snj8ynl102">
              Ver matriz de permissões
            </Button>
          </div>
        </Panel>}

      {tab === 'security' && <Panel title="Eventos de segurança" data-id="element-2320" data-mp="2v3us7irlt5cf">
          <ul className="space-y-2" data-id="element-2321" data-mp="2qccud3kvnkvo">
            {[['Entrada no iPhone 15 · São Paulo', 'hoje, 06:42'], ['Senha alterada', '02/08/2026'], ['Tentativa recusada · Chrome', '30/08/2026']].map(([label, when]) => <li key={label} className="flex items-baseline justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-2322" data-mp="2h9tsomstveb0">
                <span className="text-[12.5px] text-deep" data-id="element-2323" data-mp="27e0g53f3zkt8">{label}</span>
                <span className="bee-num text-[11.5px] text-muted" data-id="element-2324" data-mp="23dgmi9tquzbu">{when}</span>
              </li>)}
          </ul>
        </Panel>}

      <ConfirmationModal open={confirm} destructive title="Suspender esta conta?" body={`${user.name} perde o acesso imediatamente e o conteúdo público sai do ar. A ação é registrada na auditoria com o seu nome.`} confirmLabel="Suspender conta" requireText="SUSPENDER" onConfirm={() => {
      recordAudit({
        action: {
          'pt-BR': 'Suspendeu conta',
          'en-US': 'Suspended account'
        },
        entity: user.id,
        context: {
          'pt-BR': 'Detalhe do usuário',
          'en-US': 'User detail'
        },
        before: 'active',
        after: 'suspended',
        severity: 'sensitive'
      });
      setConfirm(false);
    }} onClose={() => setConfirm(false)} data-id="element-2325" data-mp="1ksgjspptxsd" />
    </div>;
}